import { CommonDtoKeysEnum } from '@zaoblako/shared/common/enums/common-dto-keys.enum';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { ReportFilterTypeEnum } from '@zaoblako/shared/common/enums/report-filter-type.enum';
import { ReportFilterGroupEnum } from '@zaoblako/shared/common/enums/report-filter-group.enum';
import type { ReportFilterDescriptorInterface } from '@zaoblako/shared/common/dtos/report/report-filter-descriptor.dto';
import type { ReportFilterApiTargetEnum } from '@zaoblako/shared/common/enums/report-filter-api-target.enum';
import type { StandardDropdownFiltersInterface } from '@zaoblako/shared/common/dtos/standard-dropdown/standard-dropdown-filters.dto';

import * as yup from 'yup';

/** {@link PaginationQueryInterface.limit} default vaue */
export const DEFAULT_LIMIT = 100;

/** {@link PaginationQueryInterface.offset} default value */
export const DEFAULT_OFFSET = 0;

/** {@link PaginationQueryInterface.limit} maximum allowed value */
export const MAX_PAGINATION_LIMIT = 500;

/** Pagination query parameters */
export interface PaginationQueryInterface {
  /** How many items need to take ({@link DEFAULT_LIMIT} by default) */
  limit?: number;
  /** How many items need to skip ({@link DEFAULT_OFFSET} by default) */
  offset?: number;
}

const LIMIT_LABEL = 'Отобрать элементов';

const OFFSET_LABEL = 'Пропустить элементов';

/** Pagination query parameters - DTO schema */
export const PaginationQueryDto = SerdeDtoClass.create<PaginationQueryInterface>(CommonDtoKeysEnum.PAGINATION_QUERY_DTO, {
  limit: {
    optional: true,
    label: LIMIT_LABEL,
    default: DEFAULT_LIMIT,
    serializer: yup
      .number()
      .integer(DtoMessageEnum.NOT_INTEGER)
      .min(1, 'Список не может содержать меньше 1 элемента')
      .max(MAX_PAGINATION_LIMIT, DtoMessageEnum.PAGINATION_LIMIT_TOO_BIG),
  },
  offset: {
    optional: true,
    label: OFFSET_LABEL,
    default: DEFAULT_OFFSET,
    serializer: yup
      .number()
      .integer(DtoMessageEnum.NOT_INTEGER)
      .min(0, 'Нельзя пропустить отрицательное число элементов'),
  },
});

/**
 * Get standard report filters list for {@link PaginationQueryInterface}
 *
 * @param targets API targets
 *
 * @returns Filter descriptors list
 */
export function getPaginationQueryDtoFilters(targets: ReportFilterApiTargetEnum[]): ReportFilterDescriptorInterface[] {
  return [
    {
      targets,
      title: LIMIT_LABEL,
      key: 'limit',
      type: ReportFilterTypeEnum.NUMBER,
      group: ReportFilterGroupEnum.PAGINATION_LIMIT,
      defaultValue: DEFAULT_LIMIT,
    },
    {
      targets,
      title: OFFSET_LABEL,
      key: 'offset',
      type: ReportFilterTypeEnum.NUMBER,
      group: ReportFilterGroupEnum.PAGINATION_OFFSET,
      defaultValue: DEFAULT_OFFSET,
    },
  ];
}

/**
 * Get part of {@link StandardDropdownFiltersInterface} for {@link PaginationQueryInterface}
 *
 * @returns Partial starndard dropdown filters schema
 */
export function getStandardDropdownFilters(): Pick<StandardDropdownFiltersInterface, 'limit' | 'offset'> {
  return {
    limit: 'limit',
    offset: 'offset',
  };
}
